import { Injectable, signal } from '@angular/core';
import {
  AccountInsight,
  AccountRisk,
  AccountTrendPoint,
  AssetAccount,
  AssetAccountCategory,
  ClientAccountsState,
  LiabilityAccount,
  LiabilityAccountCategory,
} from './client-accounts.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers — same djb2 pattern as client-finances-state.service
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(id: string, key: string, options: readonly T[]): T {
  const h = djb2(`${id}:${key}`);
  return options[h % options.length];
}

function seedInt(id: string, key: string, min: number, max: number): number {
  const h = djb2(`${id}:${key}`);
  return min + (h % (max - min + 1));
}

function seedFloat(id: string, key: string, min: number, max: number, decimals = 2): number {
  const h = djb2(`${id}:${key}`);
  const raw = min + (h % 10000) / 10000 * (max - min);
  return parseFloat(raw.toFixed(decimals));
}

function isoDate(daysFromToday: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromToday);
  return d.toISOString().slice(0, 10);
}

function seedLast4(id: string, key: string): string {
  return String(seedInt(id, key, 1000, 9999));
}

// ---------------------------------------------------------------------------
// Lookup tables
// ---------------------------------------------------------------------------

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ASSET_INSTITUTIONS: Record<AssetAccountCategory, string[]> = {
  checking:     ['Chase Bank', 'Bank of America', 'Wells Fargo', 'Citibank', 'US Bank'],
  savings:      ['Ally Bank', 'Marcus by Goldman', 'Discover Bank', 'Capital One 360', 'SoFi'],
  investment:   ['Fidelity', 'Charles Schwab', 'Vanguard', 'TD Ameritrade', 'E*TRADE'],
  retirement:   ['Fidelity', 'Vanguard', 'T. Rowe Price', 'TIAA', 'Principal Financial'],
  real_estate:  ['Zillow Estimate', 'County Assessor', 'Redfin Estimate'],
  other_asset:  ['Personal Property', 'Vehicle Value', 'Business Interest'],
};

const ASSET_NAMES: Record<AssetAccountCategory, string[]> = {
  checking:     ['Primary Checking', 'Joint Checking', 'Business Checking', 'Secondary Checking'],
  savings:      ['High-Yield Savings', 'Emergency Fund', 'Vacation Fund', 'General Savings'],
  investment:   ['Brokerage Account', 'Taxable Investment', 'Individual Portfolio', 'Growth Portfolio'],
  retirement:   ['401(k)', 'Traditional IRA', 'Roth IRA', '403(b)', 'SEP-IRA'],
  real_estate:  ['Primary Residence', 'Rental Property', 'Vacation Home'],
  other_asset:  ['Vehicle', 'Collectibles', 'Business Equity'],
};

const LIABILITY_INSTITUTIONS: Record<LiabilityAccountCategory, string[]> = {
  credit_card:     ['Chase Sapphire', 'Citi Double Cash', 'Capital One Venture', 'Amex Gold', 'Discover It'],
  student_loan:    ['Federal Student Aid', 'Sallie Mae', 'Navient', 'College Ave'],
  medical_debt:    ['Regional Medical Center', 'City Hospital', 'Urgent Care Partners'],
  personal_loan:   ['Marcus by Goldman', 'LightStream', 'SoFi Personal', 'Upstart'],
  auto_loan:       ['Chase Auto', 'Capital One Auto', 'Bank of America Auto', 'Credit Union Auto'],
  mortgage:        ['Chase Mortgage', 'Wells Fargo Home', 'Quicken Loans', 'Bank of America Mortgage'],
  other_liability: ['Personal Loan', 'Family Loan', 'Line of Credit'],
};

const LIABILITY_NAMES: Record<LiabilityAccountCategory, string[]> = {
  credit_card:     ['Rewards Card', 'Travel Card', 'Cash Back Card', 'Balance Transfer Card'],
  student_loan:    ['Federal Subsidized Loan', 'Federal Unsubsidized Loan', 'Private Student Loan', 'Graduate PLUS Loan'],
  medical_debt:    ['Hospital Bill', 'Medical Procedure', 'Dental Bill'],
  personal_loan:   ['Personal Loan', 'Debt Consolidation Loan', 'Home Improvement Loan'],
  auto_loan:       ['Auto Loan', 'Car Loan', 'Vehicle Financing'],
  mortgage:        ['Primary Mortgage', '30-Year Fixed Mortgage', '15-Year Fixed Mortgage', 'ARM Mortgage'],
  other_liability: ['Line of Credit', 'Family Loan', 'Other Debt'],
};

// ---------------------------------------------------------------------------
// Asset seeding
// ---------------------------------------------------------------------------

const ASSET_CATEGORIES: AssetAccountCategory[] = ['checking', 'savings', 'investment', 'retirement', 'real_estate'];

const ASSET_BALANCE_RANGES: Record<AssetAccountCategory, [number, number]> = {
  checking:    [2000,   25000],
  savings:     [1000,   80000],
  investment:  [5000,  150000],
  retirement:  [10000, 450000],
  real_estate: [50000, 600000],
  other_asset: [1000,   30000],
};

function seedAssets(id: string): AssetAccount[] {
  const count = seedInt(id, 'asset_count', 3, 6);
  const usedCategories = new Set<string>();
  const assets: AssetAccount[] = [];

  for (let i = 0; i < count; i++) {
    const category = seedPick<AssetAccountCategory>(id, `asset_cat_${i}`, ASSET_CATEGORIES);
    const institution = seedPick(id, `asset_inst_${i}`, ASSET_INSTITUTIONS[category]);
    const nameOptions = ASSET_NAMES[category];
    const name = seedPick(id, `asset_name_${i}`, nameOptions);
    const [balMin, balMax] = ASSET_BALANCE_RANGES[category];
    const balance = seedInt(id, `asset_bal_${i}`, balMin, balMax);
    const ytdChangeDollar = seedInt(id, `asset_ytd_${i}`, -5000, 25000);
    const ytdChangePct = balance > 0 ? parseFloat(((ytdChangeDollar / (balance - ytdChangeDollar)) * 100).toFixed(1)) : 0;
    const daysAgo = seedInt(id, `asset_age_${i}`, 0, 120);
    const uniqueKey = `${category}-${i}`;
    usedCategories.add(uniqueKey);

    assets.push({
      id: `asset-${i}`,
      name,
      institution,
      last4: seedLast4(id, `asset_last4_${i}`),
      category,
      balance,
      ytdChangeDollar,
      ytdChangePct,
      lastUpdated: isoDate(-daysAgo),
    });
  }

  return assets;
}

// ---------------------------------------------------------------------------
// Liability seeding
// ---------------------------------------------------------------------------

const LIABILITY_CATEGORIES: LiabilityAccountCategory[] = [
  'credit_card', 'student_loan', 'personal_loan', 'auto_loan', 'mortgage',
];

const LIABILITY_BALANCE_RANGES: Record<LiabilityAccountCategory, [number, number]> = {
  credit_card:     [500,   15000],
  student_loan:    [5000,  80000],
  medical_debt:    [200,   12000],
  personal_loan:   [1000,  25000],
  auto_loan:       [5000,  45000],
  mortgage:        [80000, 550000],
  other_liability: [500,   10000],
};

const REVOLVING_CATEGORIES: LiabilityAccountCategory[] = ['credit_card'];

function computeRisk(
  category: LiabilityAccountCategory,
  balance: number,
  apr: number | null,
  utilization: number | null,
  daysAgo: number
): AccountRisk {
  if (daysAgo > 90) return 'caution';
  if (utilization !== null && utilization > 0.5) return 'risk';
  if (utilization !== null && utilization > 0.3) return 'caution';
  if (apr !== null && apr > 20) return 'risk';
  if (apr !== null && apr > 15) return 'caution';
  if (category === 'mortgage' && balance > 400000) return 'caution';
  return 'healthy';
}

function seedLiabilities(id: string): LiabilityAccount[] {
  const count = seedInt(id, 'liab_count', 3, 7);
  const liabilities: LiabilityAccount[] = [];

  for (let i = 0; i < count; i++) {
    const category = seedPick<LiabilityAccountCategory>(id, `liab_cat_${i}`, LIABILITY_CATEGORIES);
    const institution = seedPick(id, `liab_inst_${i}`, LIABILITY_INSTITUTIONS[category]);
    const name = seedPick(id, `liab_name_${i}`, LIABILITY_NAMES[category]);
    const [balMin, balMax] = LIABILITY_BALANCE_RANGES[category];
    const balance = seedInt(id, `liab_bal_${i}`, balMin, balMax);

    const isRevolving = REVOLVING_CATEGORIES.includes(category);
    const apr = category === 'medical_debt' ? null : seedFloat(id, `liab_apr_${i}`, 3.5, 24.99);
    const creditLimit = isRevolving ? seedInt(id, `liab_limit_${i}`, balance + 500, balance + 15000) : null;
    const utilization = isRevolving && creditLimit ? parseFloat((balance / creditLimit).toFixed(3)) : null;
    const minPayment = balance < 500 ? null : seedInt(id, `liab_min_${i}`, 25, 500);

    const ytdChangeDollar = seedInt(id, `liab_ytd_${i}`, -8000, 3000);
    const ytdChangePct = balance > 0 ? parseFloat(((ytdChangeDollar / Math.max(balance - ytdChangeDollar, 1)) * 100).toFixed(1)) : 0;
    const daysAgo = seedInt(id, `liab_age_${i}`, 0, 150);
    const risk = computeRisk(category, balance, apr, utilization, daysAgo);

    liabilities.push({
      id: `liab-${i}`,
      name,
      institution,
      last4: seedLast4(id, `liab_last4_${i}`),
      category,
      balance,
      apr,
      creditLimit,
      utilization,
      minPayment,
      ytdChangeDollar,
      ytdChangePct,
      lastUpdated: isoDate(-daysAgo),
      risk,
    });
  }

  return liabilities;
}

// ---------------------------------------------------------------------------
// Trend seeding
// ---------------------------------------------------------------------------

function seedTrends(
  id: string,
  totalAssets: number,
  totalLiabilities: number
): AccountTrendPoint[] {
  const now = new Date();
  const points: AccountTrendPoint[] = [];

  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = `${MONTH_LABELS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
    const assetVariance = seedInt(id, `trend_asset_${i}`, -8000, 12000);
    const liabVariance = seedInt(id, `trend_liab_${i}`, -3000, 5000);
    const assets = Math.max(0, totalAssets + assetVariance);
    const liabilities = Math.max(0, totalLiabilities + liabVariance);
    points.push({ month: label, assets, liabilities, netWorth: assets - liabilities });
  }

  // Last point matches current totals exactly
  points[11] = {
    month: points[11].month,
    assets: totalAssets,
    liabilities: totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
  };

  return points;
}

// ---------------------------------------------------------------------------
// Insight derivation
// ---------------------------------------------------------------------------

function deriveInsights(
  assets: AssetAccount[],
  liabilities: LiabilityAccount[]
): AccountInsight[] {
  const insights: AccountInsight[] = [];

  const highUtilization = liabilities.filter(
    l => l.utilization !== null && l.utilization > 0.3
  );
  if (highUtilization.length > 0) {
    const worst = highUtilization.sort((a, b) => (b.utilization ?? 0) - (a.utilization ?? 0))[0];
    const pct = Math.round((worst.utilization ?? 0) * 100);
    insights.push({
      id: 'ins-utilization',
      icon: 'pi pi-credit-card',
      title: 'High Credit Utilization',
      body: `${worst.name} is at ${pct}% utilization. Lenders prefer below 30%. Reducing the balance could improve credit score within 1–2 billing cycles.`,
      severity: pct > 50 ? 'danger' : 'warn',
    });
  }

  const highApr = liabilities.filter(l => l.apr !== null && l.apr > 20);
  if (highApr.length > 0) {
    const names = highApr.map(l => l.name).join(', ');
    insights.push({
      id: 'ins-high-apr',
      icon: 'pi pi-percentage',
      title: `High APR ${highApr.length > 1 ? 'Accounts' : 'Account'}`,
      body: `${names} ${highApr.length === 1 ? 'carries' : 'carry'} APR above 20%. Refinancing or accelerated paydown could significantly reduce interest cost.`,
      severity: 'danger',
    });
  }

  const risingDebt = liabilities.filter(l => l.ytdChangeDollar > 2000);
  if (risingDebt.length > 0) {
    const names = risingDebt.map(l => l.name).join(', ');
    insights.push({
      id: 'ins-rising-debt',
      icon: 'pi pi-arrow-up',
      title: 'Rising Debt Balance',
      body: `${names} ${risingDebt.length === 1 ? 'has' : 'have'} increased by more than $2,000 YTD. Review spending patterns and consider a paydown strategy.`,
      severity: 'warn',
    });
  }

  const staleAccounts = [...assets, ...liabilities].filter(a => {
    const updated = new Date(a.lastUpdated);
    const daysDiff = (Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff > 90;
  });
  if (staleAccounts.length > 0) {
    insights.push({
      id: 'ins-stale',
      icon: 'pi pi-clock',
      title: 'Stale Account Data',
      body: `${staleAccounts.length} account${staleAccounts.length > 1 ? 's have' : ' has'} not been updated in over 90 days. Balances may not reflect current values.`,
      severity: 'warn',
    });
  }

  if (insights.length === 0) {
    insights.push({
      id: 'ins-healthy',
      icon: 'pi pi-check-circle',
      title: 'Accounts in Good Standing',
      body: 'No high-risk accounts, elevated APRs, or stale data detected. Account health looks strong.',
      severity: 'info',
    });
  }

  return insights;
}

// ---------------------------------------------------------------------------
// Main seed function
// ---------------------------------------------------------------------------

function seedAccounts(id: string): ClientAccountsState {
  const assets = seedAssets(id);
  const liabilities = seedLiabilities(id);
  const totalAssets = assets.reduce((s, a) => s + a.balance, 0);
  const totalLiabilities = liabilities.reduce((s, l) => s + l.balance, 0);
  const trends = seedTrends(id, totalAssets, totalLiabilities);
  const insights = deriveInsights(assets, liabilities);

  return { assets, liabilities, trends, insights };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class ClientAccountsStateService {
  private readonly _store = signal<Record<string, ClientAccountsState>>({});

  readonly storeSnapshot = this._store.asReadonly();

  ensureState(sessionId: string): void {
    if (this._store()[sessionId]) return;
    this._store.update(s => ({ ...s, [sessionId]: seedAccounts(sessionId) }));
  }
}
